import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import {
  Zap, ArrowLeft, Plus, Search, Edit, Trash2, Shield, Users,
  Radio, ChevronDown, ChevronUp, Check, X, Loader2, Lock, Mail,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ems_token')}`,
});

const EMS_ROLES = [
  { id: 'super_admin', label: 'Super Admin', desc: 'Full system access', color: 'bg-red-500/20 text-red-600 dark:text-red-300 border-red-500/30' },
  { id: 'hub_admin', label: 'Hub Admin', desc: 'Hub management', color: 'bg-blue-500/20 text-blue-600 dark:text-blue-300 border-blue-500/30' },
  { id: 'simcel', label: 'Simcell', desc: 'Exercise controller', color: 'bg-amber-500/20 text-amber-600 dark:text-amber-300 border-amber-500/30' },
  { id: 'observer', label: 'Observer', desc: 'Read-only access', color: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 border-emerald-500/30' },
  { id: 'participant', label: 'Participant', desc: 'Exercise player', color: 'bg-purple-500/20 text-purple-600 dark:text-purple-300 border-purple-500/30' },
];

// V14.10 — Media Portal roles are unified 1:1 with EMS (super_admin / simcel /
// observer / participant). The backend auto-derives MP role from the EMS role,
// so this list mirrors EMS_ROLES (minus hub_admin which is hub-only).
const MP_ROLES = [
  { id: 'super_admin', label: 'Super Admin', desc: 'Full Media Portal access', color: 'bg-red-500/20 text-red-600 dark:text-red-300 border-red-500/30' },
  { id: 'simcel', label: 'Simcell', desc: 'Author & moderate content', color: 'bg-amber-500/20 text-amber-600 dark:text-amber-300 border-amber-500/30' },
  { id: 'observer', label: 'Observer', desc: 'Read-only', color: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 border-emerald-500/30' },
  { id: 'participant', label: 'Participant', desc: 'Live Display kiosk only', color: 'bg-purple-500/20 text-purple-600 dark:text-purple-300 border-purple-500/30' },
];

const RoleChip = ({ role, active, onClick }) => {
  const config = [...EMS_ROLES, ...MP_ROLES].find(r => r.id === role) || { label: role, color: 'bg-muted text-foreground border-border' };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border transition-all ${
        active ? config.color : 'bg-background text-foreground/70 border-border hover:border-primary/50 hover:text-foreground'
      }`}
      data-testid={`role-chip-${role}`}
    >
      {active && <Check className="w-3 h-3" />}
      {config.label}
    </button>
  );
};

const HubUsersPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { isDark } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', roles: [], mp_roles: [], status: 'active',
  });
  const [sendInvite, setSendInvite] = useState(false);
  const [inviting, setInviting] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/users`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setUsers(Array.isArray(data) ? data : []);
      }
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', roles: ['participant'], mp_roles: [], status: 'active' });
    setShowDialog(true);
  };

  // V14.10 — translate legacy MP role IDs (admin/author/viewer) to the
  // unified set (super_admin/simcel/participant) so the chips stay in sync
  // when editing old records.
  const normalizeMpRoles = (mpRoles) => {
    const map = { admin: 'super_admin', author: 'simcel', viewer: 'participant' };
    return (mpRoles || []).map(r => map[r] || r);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '', email: user.email || '', password: '',
      roles: user.roles || [], mp_roles: normalizeMpRoles(user.mp_roles), status: user.status || 'active',
    });
    setShowDialog(true);
  };

  const toggleRole = (roleId, type) => {
    const key = type === 'ems' ? 'roles' : 'mp_roles';
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].includes(roleId)
        ? prev[key].filter(r => r !== roleId)
        : [...prev[key], roleId],
    }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) { toast.error('Name and email required'); return; }
    if (!editingUser && !formData.password) { toast.error('Password required for new user'); return; }
    // V14.13.2 — Allow MP-only users. Previously this required at least one
    // EMS role, which blocked legitimate "media portal only" accounts.
    if ((formData.roles?.length || 0) === 0 && (formData.mp_roles?.length || 0) === 0) {
      toast.error('Select at least one role (EMS or Media Portal)');
      return;
    }
    setSaving(true);
    try {
      if (editingUser) {
        const res = await fetch(`${API_URL}/users/${editingUser.user_id}`, {
          method: 'PUT', headers: getHeaders(),
          body: JSON.stringify({ name: formData.name, email: formData.email, roles: formData.roles, mp_roles: formData.mp_roles, status: formData.status, ...(formData.password ? { password: formData.password } : {}) }),
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
        toast.success('User updated');
      } else {
        const res = await fetch(`${API_URL}/users`, {
          method: 'POST', headers: getHeaders(),
          body: JSON.stringify(formData),
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
        const newUser = await res.json();
        toast.success('User created');
        // Send invite if checkbox is checked
        if (sendInvite) {
          try {
            const inviteRes = await fetch(`${API_URL}/users/invite`, {
              method: 'POST', headers: getHeaders(),
              body: JSON.stringify({ user_id: newUser.user_id, email: formData.email, temp_password: formData.password }),
            });
            if (inviteRes.ok) toast.success(`Invite email sent to ${formData.email}`);
            else { const d = await inviteRes.json(); toast.error(d.detail || 'Failed to send invite'); }
          } catch { toast.error('Failed to send invite email'); }
        }
      }
      setShowDialog(false);
      setSendInvite(false);
      loadUsers();
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleInviteUser = async (user) => {
    setInviting(user.user_id);
    try {
      const res = await fetch(`${API_URL}/users/invite`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ user_id: user.user_id, email: user.email }),
      });
      if (res.ok) toast.success(`Invite sent to ${user.email}`);
      else { const d = await res.json(); toast.error(d.detail || 'Failed to send invite'); }
    } catch { toast.error('Failed to send invite'); }
    finally { setInviting(null); }
  };

  const handleDelete = async (user) => {
    if (user.user_id === currentUser?.user_id) { toast.error("Can't delete yourself"); return; }
    if (!window.confirm(`Delete "${user.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/users/${user.user_id}`, { method: 'DELETE', headers: getHeaders() });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      toast.success('User deleted');
      loadUsers();
    } catch (err) { toast.error(err.message || 'Delete failed'); }
  };

  const handleResetPassword = async (user) => {
    const newPw = window.prompt(`Set new password for ${user.name}:`, '');
    if (!newPw || newPw.length < 6) { if (newPw !== null) toast.error('Min 6 characters'); return; }
    try {
      const res = await fetch(`${API_URL}/users/${user.user_id}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ password: newPw }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Password reset for ${user.name}`);
    } catch { toast.error('Reset failed'); }
  };

  const getAppAccess = (user) => {
    const ems = user.roles?.length > 0;
    const mp = user.mp_roles?.length > 0;
    return { ems, mp };
  };

  const bg = isDark ? 'bg-[#06070a]' : 'bg-muted';
  const text = isDark ? 'text-white' : 'text-foreground';
  const textMuted = isDark ? 'text-white/40' : 'text-muted-foreground';
  const textSubtle = isDark ? 'text-white/30' : 'text-muted-foreground';
  const textFaintest = isDark ? 'text-white/20' : 'text-muted-foreground';
  const border = isDark ? 'border-white/[0.06]' : 'border-border';
  const cardBg = isDark ? 'bg-white/[0.02]' : 'bg-white';
  const inputBg = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-white border-border text-foreground placeholder:text-muted-foreground';
  const hoverBg = isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-muted';
  const modalBg = isDark ? 'bg-[#0a0b10]' : 'bg-white';
  const btnSecondary = isDark ? 'bg-white/5 border-white/10 hover:border-white/20 text-white/60 hover:text-white' : 'bg-muted border-border hover:border-border text-muted-foreground hover:text-foreground';

  return (
    <div className={`min-h-screen ${bg} ${text}`} data-testid="hub-users-page">
      {/* Top Bar */}
      <div className={`flex items-center justify-between px-8 py-5 border-b ${border}`}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/hub')} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/5 text-white/40 hover:text-white' : 'hover:bg-muted text-muted-foreground hover:text-foreground'} transition-colors`} data-testid="hub-users-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            <span className="text-lg font-semibold">User Management</span>
          </div>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm transition-colors"
          data-testid="hub-create-user-btn">
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-8">
        {/* Search */}
        <div className="relative mb-6">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textSubtle}`} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..."
            className={`w-full ${inputBg} border rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-blue-500/50`}
            data-testid="hub-users-search" />
        </div>

        {/* User List */}
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className={`w-6 h-6 animate-spin ${textMuted}`} /></div>
        ) : filtered.length === 0 ? (
          <div className={`text-center py-12 ${textSubtle}`}><Users className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No users found</p></div>
        ) : (
          <div className="space-y-3">
            {filtered.map(user => {
              const access = getAppAccess(user);
              const isExpanded = expandedUser === user.user_id;
              const isSelf = user.user_id === currentUser?.user_id;
              return (
                <div key={user.user_id} className={`rounded-xl border ${border} ${cardBg} overflow-hidden`} data-testid={`hub-user-${user.user_id}`}>
                  <div className={`flex items-center gap-4 p-4 cursor-pointer ${hoverBg} transition`} onClick={() => setExpandedUser(isExpanded ? null : user.user_id)}>
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-purple-500/30 border ${isDark ? 'border-white/10' : 'border-border'} flex items-center justify-center text-sm font-bold ${isDark ? 'text-white/80' : 'text-foreground'} flex-shrink-0`}>
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{user.name}</p>
                        {isSelf && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">You</span>}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${user.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>{user.status}</span>
                      </div>
                      <p className={`text-xs ${textSubtle} truncate`}>{user.email}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {access.ems && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-blue-500/10 border border-blue-500/20">
                          <Shield className="w-3 h-3 text-blue-400" />
                          <span className="text-[10px] text-blue-300 font-medium">EMS</span>
                        </div>
                      )}
                      {access.mp && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500/10 border border-purple-500/20">
                          <Radio className="w-3 h-3 text-purple-400" />
                          <span className="text-[10px] text-purple-300 font-medium">MP</span>
                        </div>
                      )}
                    </div>
                    <div className="hidden md:flex gap-1 flex-shrink-0">
                      {(user.roles || []).slice(0, 2).map(r => {
                        const config = EMS_ROLES.find(er => er.id === r);
                        return <span key={r} className={`text-[10px] px-1.5 py-0.5 rounded border ${config?.color || 'bg-white/10 text-white/60 border-white/20'}`}>{config?.label || r}</span>;
                      })}
                      {(user.roles?.length || 0) > 2 && <span className={`text-[10px] ${textSubtle}`}>+{user.roles.length - 2}</span>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(user); }}
                        className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/5 text-white/40 hover:text-white' : 'hover:bg-muted text-muted-foreground hover:text-foreground'} transition`} data-testid={`hub-edit-user-${user.user_id}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      {!isSelf && (
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(user); }}
                          className="p-2 rounded-lg hover:bg-red-500/10 text-white/40 hover:text-red-400 transition" data-testid={`hub-delete-user-${user.user_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {isExpanded ? <ChevronUp className={`w-4 h-4 ${textSubtle}`} /> : <ChevronDown className={`w-4 h-4 ${textSubtle}`} />}
                  </div>

                  {isExpanded && (
                    <div className={`px-4 pb-4 pt-2 border-t ${border} space-y-3`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className={`text-xs ${textMuted} mb-2 flex items-center gap-1`}><Shield className="w-3 h-3" /> EMS Roles</p>
                          <div className="flex flex-wrap gap-1.5">
                            {(user.roles || []).map(r => {
                              const config = EMS_ROLES.find(er => er.id === r);
                              return <span key={r} className={`text-xs px-2 py-1 rounded border ${config?.color || 'bg-white/10 text-white/60 border-white/20'}`}>{config?.label || r}</span>;
                            })}
                            {(!user.roles || user.roles.length === 0) && <span className={`text-xs ${textFaintest}`}>No EMS roles</span>}
                          </div>
                        </div>
                        <div>
                          <p className={`text-xs ${textMuted} mb-2 flex items-center gap-1`}><Radio className="w-3 h-3" /> Media Portal Roles</p>
                          <div className="flex flex-wrap gap-1.5">
                            {normalizeMpRoles(user.mp_roles).map(r => {
                              const config = MP_ROLES.find(mr => mr.id === r);
                              return <span key={r} className={`text-xs px-2 py-1 rounded border ${config?.color || 'bg-muted text-muted-foreground border-border'}`}>{config?.label || r}</span>;
                            })}
                            {(!user.mp_roles || user.mp_roles.length === 0) && <span className={`text-xs ${textFaintest}`}>No MP roles</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button onClick={() => openEdit(user)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition ${btnSecondary}`}>
                          <Edit className="w-3 h-3" /> Edit Roles & Permissions
                        </button>
                        {!isSelf && (
                          <button onClick={() => handleResetPassword(user)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition ${btnSecondary}`}
                            data-testid={`hub-reset-pw-${user.user_id}`}>
                            <Lock className="w-3 h-3" /> Reset Password
                          </button>
                        )}
                        {!isSelf && (
                          <button onClick={() => handleInviteUser(user)} disabled={inviting === user.user_id}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition ${btnSecondary}`}
                            data-testid={`hub-invite-user-${user.user_id}`}>
                            {inviting === user.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                            Send Invite Email
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="hub-user-dialog">
          <div className={`w-full max-w-xl mx-4 rounded-2xl border ${border} ${modalBg} shadow-2xl max-h-[90vh] overflow-y-auto`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${border}`}>
              <h2 className="text-lg font-semibold">{editingUser ? 'Edit User' : 'Create User'}</h2>
              <button onClick={() => setShowDialog(false)} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/5 text-white/40 hover:text-white' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}`}><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-xs ${textMuted} block mb-1.5`}>Full Name</label>
                  <input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="John Smith"
                    className={`w-full ${inputBg} border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50`}
                    data-testid="hub-user-name" />
                </div>
                <div>
                  <label className={`text-xs ${textMuted} block mb-1.5`}>Email</label>
                  <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="user@example.com"
                    className={`w-full ${inputBg} border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50`}
                    data-testid="hub-user-email" />
                </div>
              </div>
              <div>
                <label className={`text-xs ${textMuted} block mb-1.5`}>{editingUser ? 'New Password (leave empty to keep)' : 'Password'}</label>
                <input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})}
                  placeholder={editingUser ? 'Leave empty to keep current' : 'Min 6 characters'}
                  className={`w-full ${inputBg} border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50`}
                  data-testid="hub-user-password" />
              </div>
              {editingUser && (
                <div>
                  <label className={`text-xs ${textMuted} block mb-1.5`}>Status</label>
                  <div className="flex gap-2">
                    {['active', 'inactive'].map(s => (
                      <button key={s} type="button" onClick={() => setFormData({...formData, status: s})}
                        className={`px-3 py-1.5 rounded-lg text-xs border transition ${
                          formData.status === s
                            ? (s === 'active' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30')
                            : isDark ? 'bg-white/[0.02] text-white/30 border-white/[0.06] hover:border-white/20' : 'bg-muted text-muted-foreground border-border hover:border-border'
                        }`}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  <label className={`text-xs ${textMuted}`}>EMS Access & Roles</label>
                </div>
                <div className={`p-3 rounded-xl border ${border} ${isDark ? 'bg-white/[0.01]' : 'bg-muted'}`}>
                  <div className="flex flex-wrap gap-2">
                    {EMS_ROLES.map(role => (
                      <RoleChip key={role.id} role={role.id} active={formData.roles.includes(role.id)}
                        onClick={() => toggleRole(role.id, 'ems')} />
                    ))}
                  </div>
                  <p className={`text-[10px] ${textFaintest} mt-2`}>Select roles for Exercise Management System access</p>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Radio className="w-4 h-4 text-purple-400" />
                  <label className={`text-xs ${textMuted}`}>Media Portal Access & Roles</label>
                </div>
                <div className={`p-3 rounded-xl border ${border} ${isDark ? 'bg-white/[0.01]' : 'bg-muted'}`}>
                  <div className="flex flex-wrap gap-2">
                    {MP_ROLES.map(role => (
                      <RoleChip key={role.id} role={role.id} active={formData.mp_roles.includes(role.id)}
                        onClick={() => toggleRole(role.id, 'mp')} />
                    ))}
                  </div>
                  <p className={`text-[10px] ${textFaintest} mt-2`}>Select roles for Media Portal access</p>
                </div>
              </div>
            </div>

            <div className={`flex items-center justify-between px-6 py-4 border-t ${border}`}>
              {!editingUser && (
                <label className="flex items-center gap-2 cursor-pointer" data-testid="send-invite-checkbox">
                  <input type="checkbox" checked={sendInvite} onChange={e => setSendInvite(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-500 text-blue-600 focus:ring-blue-500" />
                  <span className={`text-xs ${textMuted} flex items-center gap-1`}><Mail className="w-3 h-3" /> Send invite email with credentials</span>
                </label>
              )}
              {editingUser && <div />}
              <div className="flex gap-3">
                <button onClick={() => setShowDialog(false)}
                  className={`px-4 py-2 rounded-lg border text-sm transition ${btnSecondary}`}>
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm transition disabled:opacity-50"
                  data-testid="hub-user-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HubUsersPage;
